"use client";

import { ArrowRight } from "lucide-react";
import Link from "next/link";
import type { HomeHeroContent } from "@/lib/page-content-types";

const defaultHero: HomeHeroContent = {
  eyebrow: "The Reference",
  title: "Israeli wine, made easy to discover and trust",
  subtitle:
    "A complete atlas combining authoritative editorial guidance with a structured, searchable directory.",
  ctaLabel: "Explore Directory",
  ctaHref: "/directory",
  stats: [
    { value: "250+", label: "Wineries" },
    { value: "12", label: "Regions" },
    { value: "40+", label: "Varieties" },
  ],
};

export function Hero({ content = defaultHero }: { content?: HomeHeroContent }) {
  return (
    <section style={{ backgroundColor: "var(--paper)" }}>
      <div className="mx-auto max-w-screen-2xl px-8 lg:px-16">
        <div className="pb-24 pt-32 lg:pb-32 lg:pt-48">
          <div className="mx-auto max-w-5xl">
            <div className="mb-8">
              <span
                style={{
                  fontFamily: "var(--font-body)",
                  fontSize: "var(--text-xs)",
                  fontWeight: 400,
                  color: "var(--oxblood)",
                  letterSpacing: "0.15em",
                  textTransform: "uppercase",
                }}
              >
                {content.eyebrow}
              </span>
            </div>

            <h1
              className="mb-12"
              style={{
                fontFamily: "var(--font-display)",
                fontSize: "clamp(3rem, 8vw, 5rem)",
                fontWeight: 400,
                lineHeight: "var(--line-height-tight)",
                color: "var(--charcoal)",
                letterSpacing: "-0.02em",
                maxWidth: "1000px",
              }}
            >
              {content.title}
            </h1>

            <p
              className="mb-16 max-w-2xl"
              style={{
                fontFamily: "var(--font-body)",
                fontSize: "var(--text-lg)",
                lineHeight: "var(--line-height-body)",
                color: "var(--slate)",
                fontWeight: 300,
              }}
            >
              {content.subtitle}
            </p>

            <div className="mb-24">
              <Link
                href={content.ctaHref}
                className="group inline-flex items-center gap-3 px-8 py-4 transition-all duration-200 hover:opacity-85"
                style={{
                  backgroundColor: "var(--charcoal)",
                  color: "var(--paper)",
                  fontFamily: "var(--font-body)",
                  fontSize: "var(--text-sm)",
                  fontWeight: 400,
                  letterSpacing: "0.01em",
                }}
              >
                {content.ctaLabel}
                <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </div>

            <div className="flex flex-wrap gap-x-16 gap-y-8 pt-12" style={{ borderTop: "1px solid var(--stone)" }}>
              {content.stats.map((stat) => (
                <div key={stat.label}>
                  <div
                    style={{
                      fontFamily: "var(--font-display)",
                      fontSize: "var(--text-4xl)",
                      fontWeight: 300,
                      color: "var(--charcoal)",
                      lineHeight: 1,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {stat.value}
                  </div>
                  <div
                    className="mt-2"
                    style={{
                      fontFamily: "var(--font-body)",
                      fontSize: "var(--text-xs)",
                      color: "var(--slate)",
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                    }}
                  >
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
